import React, { useCallback } from 'react';
import {
  PricesAccordionSection,
  PricesSectionType,
  PricesState,
  PricesTabType,
} from 'store/state/selectors/insights/prices';
import PricesIcon from 'assets/svg/sale-history.svg';
import WidgetWrapper from 'components/widget/common/widget-wrapper';
import PricesAccordion from './prices-accordion';
import { InsightType } from 'utils/entities';
import { InsightSection } from 'store/state/insightsContext';
import { IInsightPreview } from 'components/widget/common/styled';
import CarouselStories from 'components/view-pager-to-stories';
import { useTheme } from 'hooks/useTheme';
import { Route, useRoute } from 'config/routes';
import { useLocale } from 'locale';
import Listings from 'components/widget/prices/section/listings';
import { PricesBorder } from 'components/widget/prices/section/styled';
import { PricesTableTitle, TableHeader } from './styled';
import PriceChart from 'components/widget/trends';
import WidgetCollapsedWrapper from '../../common/widget-collapsed-wrapper';
import { connect } from 'react-redux';
import { PriceCollapsedDynamic, responseDataSelector } from '../section/selectors';


interface PricesWidgetProps {
  onSetPricesState: (state: Partial<PricesState>) => void;
  data: PricesAccordionSection[];
  activeSection: PricesSectionType;
  tab: PricesTabType;
  isArea?: boolean;
  carouselData: IInsightPreview[];
  setActiveCarouselIdx: (index: number) => void;
  carouselIdx: number;
  setActiveInsightId: (id: string) => void;
  pricesItemsCount?: number;
  showTable: boolean;
  zoneName?: string;
  projectId?: string;
  projectIds?: string[];
  dynamicContentData: PriceCollapsedDynamic;
  onlyAnalystInsight?: boolean;
  isCollapsed: boolean;
  showCarousel: boolean;
}

const PricesWidget: React.FC<PricesWidgetProps> = ({
  isArea,
  onSetPricesState,
  data,
  activeSection,
  tab,
  carouselData,
  setActiveCarouselIdx,
  carouselIdx,
  setActiveInsightId,
  pricesItemsCount,
  showTable,
  zoneName,
  projectId,
  projectIds,
  dynamicContentData,
  onlyAnalystInsight,
  isCollapsed,
  showCarousel,
}) => {
  const setActiveSection = useCallback(
    (nextSection) => onSetPricesState({ accordion: nextSection }),
    [ onSetPricesState ]
  );
  const { colors } = useTheme();
  const { route } = useRoute();
  const { t } = useLocale();

  const setActiveTab = useCallback(
    (nextTab: PricesTabType) => onSetPricesState({ tab: nextTab }),
    [ onSetPricesState ]
  );

  const widgetHasCarouselData = showCarousel && carouselData && carouselData.length;

  if (!widgetHasCarouselData && onlyAnalystInsight) return null;

  const carousel = (
    <CarouselStories
      insights={carouselData}
      onReadMoreClick={setActiveInsightId}
      slideIndex={carouselIdx}
      onSlideIndexChange={(idx) => setActiveCarouselIdx(idx)}
      color={colors.insights.prices}
    />
  );

  const hideAccordion = !isArea || [
    Route.ProjectPage,
    Route.AddressPage,
    Route.LocalPage,
    Route.Sold,
    Route.UnitPage,
  ].includes(route.name);

  const wrapperProps = {
    carousel: widgetHasCarouselData ? carousel : null,
    targetId: InsightSection.Prices,
    styleType: InsightType.Prices,
    title: t('widgetTitle.prices'),
    onlyAnalystInsight,
  };

  const content = onlyAnalystInsight ? null : (
    <>
      {route.name !== Route.Sold && route.name !== Route.LocalPage ? (
        <PriceChart />
      ) : null}
      {hideAccordion ? null : (
        <PricesAccordion
          sections={data}
          setActiveSection={setActiveSection}
          setActiveTab={setActiveTab}
          activeSection={activeSection}
          activeTab={tab}
        />
      )}
      {showTable && (route.name === Route.StreetPage || route.name === Route.Sold) ? (
        <PricesBorder>
          <PricesTableTitle>{t('widget.prices.tableHeader')}</PricesTableTitle>
          <Listings projectId={projectId} projectIds={projectIds} />
        </PricesBorder>
      ) : null}
      {showTable && route.name === Route.LocalPage ? (
        <PricesBorder key={Route.LocalPage}>
          {zoneName ? (
            <TableHeader>
              <PricesIcon />{t('widget.prices.tableAreaHeader', { count: pricesItemsCount, area: zoneName })}
            </TableHeader>
          ) : null}
          <Listings showTabs={false} />
        </PricesBorder>
      ) : null}
    </>
  );

  return isCollapsed
    ? <WidgetCollapsedWrapper {...wrapperProps} dynamicContentData={dynamicContentData}>{content}</WidgetCollapsedWrapper>
    : <WidgetWrapper {...wrapperProps}>{content}</WidgetWrapper>;
};

export default connect(responseDataSelector)(PricesWidget);
